import type { Fetcher } from "../fetcher";
import type {
  CarrierFilters,
  CreateCarrierDto,
  UpdateCarrierDto,
  CarrierDirectionPayload,
  CreateCarrierDirectionDto,
  UpdateCarrierDirectionDto,
  UpdateCarrierDirectionMetaDto,
  SearchCarriersByDirectionParams,
} from "./types";
import type {
  Carrier,
  CarrierByDirection,
  CarrierDirection,
} from "../entities/carrier";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";

/**
 * Repository for carrier-related endpoints. Backend handles localisation via
 * the `X-Language` header set by the fetcher — no `locale` argument needed.
 */
export function createCarriersRepository(fetcher: Fetcher) {
  return {
    async list(filters: CarrierFilters = {}): Promise<{
      items: Carrier[];
      pagination: { total: number };
    }> {
      const params = new URLSearchParams();
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);

      const query = params.toString();
      const url = query ? `/carriers?${query}` : "/carriers";

      const response = await fetcher<ApiCollectionResponse<Carrier>>(url);
      return {
        items: response.data?.items ?? [],
        pagination: {
          total: response.data?.pagination?.total ?? (response.data?.items?.length ?? 0),
        },
      };
    },

    async get(id: string): Promise<Carrier> {
      const response = await fetcher<ApiSingleResponse<Carrier>>(`/carriers/${id}`);
      return response.data;
    },

    async create(payload: CreateCarrierDto): Promise<Carrier> {
      const response = await fetcher<ApiSingleResponse<Carrier>>("/carriers", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async update(id: string, payload: UpdateCarrierDto): Promise<Carrier> {
      const response = await fetcher<ApiSingleResponse<Carrier>>(`/carriers/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async listDirections(
      carrierId: string,
      filters: { page?: number; limit?: number } = {},
    ): Promise<{ items: CarrierDirection[]; total: number }> {
      const params = new URLSearchParams();
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));

      const query = params.toString();
      const url = query
        ? `/carriers/${carrierId}/directions?${query}`
        : `/carriers/${carrierId}/directions`;

      const response = await fetcher<ApiCollectionResponse<CarrierDirection>>(url);
      const items = response.data?.items ?? [];
      return {
        items,
        total: response.data?.pagination?.total ?? items.length,
      };
    },

    async addDirection(id: string, payload: CreateCarrierDirectionDto): Promise<CarrierDirection> {
      const response = await fetcher<ApiSingleResponse<CarrierDirection>>(
        `/carriers/${id}/directions`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateDirection(
      carrierId: string,
      directionId: string,
      payload:
        | UpdateCarrierDirectionDto
        | CarrierDirectionPayload
        | UpdateCarrierDirectionMetaDto,
    ): Promise<CarrierDirection> {
      const response = await fetcher<ApiSingleResponse<CarrierDirection>>(
        `/carriers/${carrierId}/directions/${directionId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteDirection(carrierId: string, directionId: string): Promise<void> {
      await fetcher(
        `/carriers/${carrierId}/directions/${directionId}`,
        { method: "DELETE" },
      );
    },

    /**
     * Cross-carrier lookup: "carriers that run TR → TM". Resolves country
     * codes server-side. `transportType` / `licenceType` filter on the
     * per-carrier metadata arrays (handbook codes) — see B8 / classifiers.
     */
    async searchByDirection(
      params: SearchCarriersByDirectionParams,
    ): Promise<CarrierByDirection[]> {
      const qs = new URLSearchParams();
      qs.append("from", params.from);
      qs.append("to", params.to);
      if (params.transportType) qs.append("transportType", params.transportType);
      if (params.licenceType) qs.append("licenceType", params.licenceType);
      const response = await fetcher<ApiCollectionResponse<CarrierByDirection>>(
        `/carriers/search-by-direction?${qs.toString()}`,
      );
      return response.data?.items ?? [];
    },
  };
}
